import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, Select, message, Button, Row, Col, Alert, Upload } from 'antd';
import type { User, CreateUserInput, UpdateUserInput, UserRole, UserStatus, ImageMetadata } from '../../../types';
import type { Contact, WorkspaceReference } from '../../../types/common';
import type { Workspace } from '../../../types/workspaces';
import { addUser, updateUser } from '../../../api/users';
import { getWorkspaces } from '../../../api/workspaces';
import ContactsFormSection from '../../common/ContactsFormSection';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UserOutlined, MailOutlined, LockOutlined, UploadOutlined, PhoneOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import CurrencySelector from '../../common/CurrencySelector';
import LanguageSelector from '../../common/LanguageSelector';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import type { UploadFile } from 'antd/es/upload/interface';
import { useAuth } from '@/contexts/AuthContext';

const staticRoleOptions = [
  { value: 'employee', label: 'Employee' },
  { value: 'resident', label: 'Resident' },
  { value: 'vendor', label: 'Vendor' },
];

interface UserModalProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess: () => void;
  user?: User;
}

interface UserFormValues {
  email: string;
  password: string;
  displayName: string;
  username: string;
  status: UserStatus;
  contacts: Contact[];
  roles: UserRole[];
  workspace: WorkspaceReference;
  workspaceId?: string;
  language: 'en' | 'es' | 'pt';
  currency: 'USD' | 'CLP' | 'BRL';
  profilePicture?: ImageMetadata;
  phoneNumber?: string;
}

// Helper function to generate username from display name
const generateUsername = (displayName: string): string => {
  return displayName
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remove accents
    .replace(/[^a-z0-9]/g, '-') // Replace non-alphanumeric with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
    .replace(/^-|-$/g, '') // Remove leading/trailing hyphens
    .substring(0, 30); // Limit length
};

const UserModal: React.FC<UserModalProps> = ({ visible, onCancel, onSuccess, user }) => {
  const [form] = Form.useForm<UserFormValues>();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const { t } = useTranslation(['common', 'users']);
  const { firestoreUser } = useAuth();

  const addUserMutation = useMutation({
    mutationFn: (data: CreateUserInput) => addUser(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      message.success(t('users:messages.createSuccess'));
      form.resetFields();
      setError(null);
      onSuccess();
    },
    onError: (error: Error) => {
      if (error.message === 'USER_EXISTS') {
        setError(t('users:errors.userExists'));
      } else {
        setError(error.message || t('users:errors.createFailed'));
      }
    },
  });

  const updateUserMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateUserInput }) => 
      updateUser(id, data, firestoreUser!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      message.success(t('users:messages.updateSuccess'));
      form.resetFields();
      onSuccess();
    },
    onError: (error: Error) => {
      message.error(error.message || t('users:errors.updateFailed'));
    },
  });

  useEffect(() => {
    if (visible) {
      form.resetFields();
      if (user) {
        const formValues = {
          ...user,
          roles: user.roles || [],
          profilePicture: user.profilePicture ? {
            id: user.profilePicture.id,
            name: user.profilePicture.name,
            type: user.profilePicture.type,
            size: user.profilePicture.size,
            path: user.profilePicture.path,
            url: user.profilePicture.url,
            uploadedAt: user.profilePicture.uploadedAt,
            isPrimary: user.profilePicture.isPrimary
          } : undefined
        };
        form.setFieldsValue(formValues);
        // Set initial file list if user has a profile picture
        if (user.profilePicture) {
          setFileList([{
            uid: user.profilePicture.id,
            name: user.profilePicture.name,
            status: 'done',
            url: user.profilePicture.url,
          }]);
        } else {
          setFileList([]);
        }
      } else {
        setFileList([]);
      }
      // Fetch all workspaces for workspace selection
      getWorkspaces().then(res => setWorkspaces(res.workspaces as any));
    }
  }, [visible, user, form]);

  const handleWorkspaceChange = (workspaceId: string) => {
    const workspace = workspaces.find(w => w.id === workspaceId);
    if (workspace) {
      const workspaceData = {
        id: workspace.id,
        name: workspace.name,
        slug: workspace.slug
      };
      form.setFieldsValue({
        workspace: workspaceData,
        workspaceId: workspace.id,
        language: workspace.language as 'en' | 'es' | 'pt',
        currency: workspace.currency as 'USD' | 'CLP' | 'BRL'
      });
    }
  };

  // Handle display name change to generate username
  const handleDisplayNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const displayName = e.target.value;
    if (displayName && !user) { // Only generate for new users
      const username = generateUsername(displayName);
      form.setFieldsValue({ username });
    }
  };

  const handleProfilePictureUpload = async (file: File) => {
    try {
      setUploading(true);
      const timestamp = Date.now();
      const fileExtension = file.name.split('.').pop();
      const fileName = `${timestamp}.${fileExtension}`;
      
      // Get username from form
      const username = form.getFieldValue('username');
      
      if (!username) {
        message.error(t('users:errors.missingUsername'));
        return false;
      }

      // Use username in storage path
      const storagePath = `users/${username}/profile-pictures/${fileName}`;
      const storage = getStorage();
      const storageRef = ref(storage, storagePath);
      
      // Upload file with metadata
      const metadata = {
        contentType: file.type,
        customMetadata: {
          uploadedBy: user?.id || 'temp',
          uploadedAt: timestamp.toString(),
          originalName: file.name,
          username: username,
          isTemporary: 'false'
        }
      };
      
      const snapshot = await uploadBytes(storageRef, file, metadata);
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      // Create image metadata object with required fields
      const imageMetadata: ImageMetadata = {
        id: snapshot.ref.name, // Use the storage reference name as ID
        name: file.name,
        type: file.type,
        size: file.size,
        path: storagePath,
        url: downloadURL,
        uploadedAt: new Date(timestamp),
        isPrimary: true
      };
      
      form.setFieldsValue({ profilePicture: imageMetadata });
      
      setFileList([{
        uid: '-1',
        name: file.name,
        status: 'done',
        url: downloadURL,
      }]);
      
      message.success(t('users:messages.uploadSuccess'));
      return false; // Prevent default upload behavior
    } catch (error: any) {
      console.error('Error uploading profile picture:', error);
      if (error.code === 'storage/unauthorized') {
        message.error(t('users:errors.uploadUnauthorized'));
      } else {
        message.error(t('users:errors.uploadFailed'));
      }
      return false;
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (values: UserFormValues) => {
    try {
      if (!firestoreUser) {
        throw new Error('User not authenticated');
      }

      setLoading(true);
      setError(null);

      const { profilePicture, workspaceId, ...restValues } = values;

      // Handle profile picture metadata
      let profilePictureMetadata: ImageMetadata | undefined;
      if (fileList.length > 0 && fileList[0].status === 'done' && fileList[0].url) {
        profilePictureMetadata = {
          id: fileList[0].uid,
          name: fileList[0].name || '',
          type: fileList[0].type || '',
          size: fileList[0].size || 0,
          path: `users/${values.username}/profile-pictures/${fileList[0].name}`,
          url: fileList[0].url,
          uploadedAt: new Date(),
          isPrimary: true
        };
      }

      // Get workspace data
      const workspace = workspaces.find(w => w.id === workspaceId);
      if (!workspace) {
        throw new Error('Invalid workspace selected');
      }

      const workspaceData = {
        id: workspace.id,
        name: workspace.name,
        slug: workspace.slug
      };

      if (user) {
        // Update existing user
        await updateUserMutation.mutateAsync({
          id: user.id,
          data: {
            ...restValues,
            profilePicture: profilePictureMetadata,
            workspace: workspaceData
          }
        });
      } else {
        // Create new user
        await addUserMutation.mutateAsync({
          ...restValues,
          profilePicture: profilePictureMetadata,
          workspace: workspaceData
        });
      }

      onSuccess();
    } catch (error: any) {
      console.error('Error submitting user:', error);
      setError(error.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setError(null);
    onCancel();
  };

  return (
    <Modal
      title={user ? t('users:edit.title') : t('users:create.title')}
      open={visible}
      onCancel={handleCancel}
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          {t('common:actions.cancel')}
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={loading}
          onClick={() => form.submit()}
        >
          {user ? t('common:actions.save') : t('common:actions.create')}
        </Button>
      ]}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={user}
      >
        {!user && (
          <>
            <Form.Item
              name="email"
              label={t('users:fields.email')}
              rules={[
                { required: true, message: t('users:validation.email.required') },
                { type: 'email', message: t('users:validation.email.invalid') },
              ]}
            >
              <Input prefix={<MailOutlined />} />
            </Form.Item>

            <Form.Item
              name="password"
              label={t('users:fields.password')}
              rules={[
                { required: true, message: t('users:validation.password.required') },
                { min: 6, message: t('users:validation.password.minLength') },
              ]}
            >
              <Input.Password prefix={<LockOutlined />} />
            </Form.Item>
          </>
        )}

        <Form.Item
          name="displayName"
          label={t('users:fields.displayName')}
          rules={[{ required: true, message: t('users:validation.displayName.required') }]}
        >
          <Input prefix={<UserOutlined />} onChange={handleDisplayNameChange} />
        </Form.Item>

        <Form.Item
          name="phoneNumber"
          label={t('users:fields.phoneNumber')}
          rules={[
            { pattern: /^\+?[\d\s-]+$/, message: t('users:validation.phoneNumber.invalid') }
          ]}
        >
          <Input prefix={<PhoneOutlined />} />
        </Form.Item>

        {!user && (
          <Form.Item
            name="username"
            label={t('users:fields.username')}
            rules={[{ required: true, message: t('users:validation.username.required') }]}
            hidden // Hide from UI but keep in form
          >
            <Input />
          </Form.Item>
        )}

        <Form.Item
          name="status"
          label={t('users:fields.status')}
          rules={[{ required: true, message: t('users:validation.status.required') }]}
        >
          <Select>
            <Select.Option value="active">{t('users:status.active')}</Select.Option>
            <Select.Option value="inactive">{t('users:status.inactive')}</Select.Option>
          </Select>
        </Form.Item>

        <Form.Item
          name="workspaceId"
          label="Workspace"
          rules={[{ required: true, message: 'Please select a workspace!' }]}
        >
          <Select
            placeholder="Select workspace"
            showSearch
            optionFilterProp="children"
            onChange={handleWorkspaceChange}
          >
            {workspaces.map(ws => (
              <Select.Option key={ws.id} value={ws.id}>{ws.name}</Select.Option>
            ))}
          </Select>
        </Form.Item>

        {/* Hidden field to store the complete workspace object */}
        <Form.Item name="workspace" hidden>
          <Input />
        </Form.Item>

        <Form.Item
          name="roles"
          label={t('users:fields.roles')}
          rules={[{ required: true, message: t('users:validation.roles.required') }]}
        >
          <Select
            mode="multiple"
            placeholder={t('users:placeholders.selectRoles')}
            options={staticRoleOptions}
          />
        </Form.Item>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="language"
              label={t('users:fields.language')}
            >
              <LanguageSelector />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="currency"
              label={t('users:fields.currency')}
            >
              <CurrencySelector />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="profilePicture"
          label={t('users:fields.profilePicture')}
        >
          <Upload
            name="profilePicture"
            listType="picture"
            maxCount={1}
            fileList={fileList}
            beforeUpload={handleProfilePictureUpload}
            onRemove={() => {
              form.setFieldsValue({ profilePicture: undefined });
              setFileList([]);
            }}
          >
            <Button icon={<UploadOutlined />} loading={uploading}>
              {uploading ? t('users:uploading') : t('users:uploadProfilePicture')}
            </Button>
          </Upload>
        </Form.Item>

        <Form.Item label={t('common:contacts', 'Contacts')}>
          <ContactsFormSection
            t={t}
          />
        </Form.Item>
      </Form>
      {error && (
        <Alert
          message={t('common:error')}
          description={error}
          type="error"
          showIcon
          style={{ marginTop: 16 }}
        />
      )}
    </Modal>
  );
};

export default UserModal; 